<template>
  <v-app>
    <AppLayout>
      <router-view />
    </AppLayout>
  </v-app>
</template>

<script setup>
import { onMounted } from "vue";
import AppLayout from "@/layouts/AppLayout.vue";
import { useColorTheme } from "@/composables/useColorTheme";

const { init } = useColorTheme();
onMounted(init);
</script>

<style>
/* ===== CSS Custom Properties ===== */
/* Light = warm/organic (default). Accent = solid navy. No gradients. */
:root {
  --c-bg: #f7f2ea;        /* warm ivory */
  --c-bg-alt: #efe6d8;    /* warm sand */
  --c-bg-card: #fffdf8;   /* warm white */
  --c-card-hover: #f3ebdd;
  --c-border: rgba(43, 37, 30, 0.12);
  --c-border-subtle: rgba(43, 37, 30, 0.08);
  --c-text-1: #262d3f;    /* deep navy ink — headings */
  --c-text-2: #2f2a23;    /* warm near-black — body */
  --c-text-3: #6c6155;    /* warm gray */
  --c-text-4: #8b8073;    /* warm muted */
  --c-text-5: #b4a896;
  --c-mesh: rgba(43, 37, 30, 0.04);
  --c-overlay: rgba(247, 242, 234, 0.82);
  --c-navbar: rgba(247, 242, 234, 0.85);
  --c-navbar-text: #262d3f;
  --c-footer: #efe6d8;
  --c-footer-text: #6c6155;
  --c-drawer-bg: #fffdf8;
  --c-drawer-text: #2f2a23;
  /* Accent — solid navy */
  --c-accent: #28344f;
  --c-accent-hover: #1b2438;
  --c-accent-text: #28344f;
  --c-accent-on: #ffffff;
  --c-accent-soft: rgba(40, 52, 79, 0.07);
  --c-accent-border: rgba(40, 52, 79, 0.2);
  --c-shadow-btn: rgba(40, 52, 79, 0.22);
  --c-shadow-card: rgba(40, 52, 79, 0.12);
  --c-danger: #b23b31;        /* エラー: 暖色の赤（クリーム地で 4.5:1 以上） */
  --c-danger-soft: rgba(178, 59, 49, 0.08);
  --c-danger-border: rgba(178, 59, 49, 0.28);
  /* Paper grain — overlay strength */
  --grain-opacity: 0.05;
  /* Typography — 見出しのみ Zen Kaku Gothic New。本文はデフォルトのまま */
  --font-heading: "Zen Kaku Gothic New", "Hiragino Kaku Gothic ProN",
    "Yu Gothic", sans-serif;
}

/* Dark = warm dark (toggle). Cream text on warm charcoal, lighter navy accent. */
html.dark {
  --c-bg: #1c1813;        /* warm charcoal */
  --c-bg-alt: #25201a;
  --c-bg-card: rgba(255, 249, 238, 0.04);
  --c-card-hover: rgba(255, 249, 238, 0.07);
  --c-border: rgba(255, 244, 228, 0.1);
  --c-border-subtle: rgba(255, 244, 228, 0.07);
  --c-text-1: #f3ead8;    /* warm cream */
  --c-text-2: #e7dcc8;
  --c-text-3: #b3a48d;
  --c-text-4: #8a7d69;
  --c-text-5: #5f5648;
  --c-mesh: rgba(255, 244, 228, 0.03);
  --c-overlay: rgba(28, 24, 19, 0.78);
  --c-navbar: rgba(28, 24, 19, 0.85);
  --c-navbar-text: #f3ead8;
  --c-footer: #16120d;
  --c-footer-text: #b3a48d;
  --c-drawer-bg: #25201a;
  --c-drawer-text: #e7dcc8;
  /* Accent — lightened navy so it stays legible on dark */
  --c-accent: #3c4d75;
  --c-accent-hover: #4a5d8a;
  --c-accent-text: #9fb1d6;
  --c-accent-on: #ffffff;
  --c-accent-soft: rgba(159, 177, 214, 0.1);
  --c-accent-border: rgba(159, 177, 214, 0.26);
  --c-shadow-btn: rgba(0, 0, 0, 0.45);
  --c-shadow-card: rgba(0, 0, 0, 0.4);
  --c-danger: #f1a094;
  --c-danger-soft: rgba(241, 160, 148, 0.1);
  --c-danger-border: rgba(241, 160, 148, 0.32);
  /* ダーク背景ではノイズが沈むため少し強める */
  --grain-opacity: 0.07;
}

/* ===== 紙の質感（グレイン）オーバーレイ ===== */
/* SVG feTurbulence をタイル状に敷いた微細ノイズ。クリック透過・全画面固定。 */
body::after {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
  opacity: var(--grain-opacity);
  background-repeat: repeat;
  background-size: 160px 160px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

/* ===== View Transitions（ページ遷移アニメーション） ===== */
/* 旧ページはフェードアウト、新ページはフェード＋わずかに浮き上がる */
::view-transition-old(root) {
  animation: vt-leave 0.2s ease both;
}

::view-transition-new(root) {
  animation: vt-enter 0.3s ease 0.05s both;
}

@keyframes vt-leave {
  to {
    opacity: 0;
  }
}

@keyframes vt-enter {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
}

/* 固定ナビバー・トップへ戻るボタン・読込バーは遷移の影響を受けず静止させる */
::view-transition-old(app-navbar),
::view-transition-new(app-navbar),
::view-transition-old(back-to-top),
::view-transition-new(back-to-top),
::view-transition-old(route-progress),
::view-transition-new(route-progress) {
  animation: none;
}

@media (prefers-reduced-motion: reduce) {
  ::view-transition-old(root),
  ::view-transition-new(root) {
    animation: none;
  }

  /* 常時ループする装飾アニメーションも停止する */
  .badge-ship,
  .scroll-line,
  .term-cursor {
    animation: none !important;
  }

  html {
    scroll-behavior: auto !important;
  }
}

/* テーマ切り替えのスムーズなトランジション */
*,
*::before,
*::after {
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease,
    color 0.3s ease;
}

/* トランジション対象外（アニメーション干渉を防ぐ） */
.badge-ship,
.scroll-line {
  transition: none !important;
}

/* ===== 見出しタイポグラフィ ===== */
/* !important は Vuetify の .text-h* ユーティリティ（!important 付き）に勝つため */
h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: var(--font-heading) !important;
  /* palt: 約物・かなを詰める（フォントが対応しない環境では無視される） */
  font-feature-settings: "palt" 1;
}

/* Vuetify ドロワー背景 */
.v-navigation-drawer {
  background: var(--c-drawer-bg) !important;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
}
</style>
